import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  FileText,
  AlertTriangle,
  Activity,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Loader2,
  ArrowLeft,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Country codes with validation rules
const COUNTRY_CODES = [
  { code: '+91', country: 'India', mobileCode: 'IN', minDigits: 10, maxDigits: 10 },
  { code: '+60', country: 'Malaysia', mobileCode: 'MY', minDigits: 8, maxDigits: 10 },
  { code: '+1', country: 'USA/Canada', mobileCode: 'US', minDigits: 10, maxDigits: 10 },
  { code: '+44', country: 'UK', mobileCode: 'GB', minDigits: 10, maxDigits: 10 },
  { code: '+61', country: 'Australia', mobileCode: 'AU', minDigits: 9, maxDigits: 9 },
  { code: '+65', country: 'Singapore', mobileCode: 'SG', minDigits: 8, maxDigits: 8 },
  { code: '+971', country: 'UAE', mobileCode: 'AE', minDigits: 9, maxDigits: 9 },
  { code: '+966', country: 'Saudi Arabia', mobileCode: 'SA', minDigits: 9, maxDigits: 9 },
];

const MERAGO_API_BASE = 'https://production.meragoapp.com:8080/merago/api/user/v1/member';

export const PatientManagementPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientActivities, setPatientActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Dialog states
  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);
  const [isEditPatientOpen, setIsEditPatientOpen] = useState(false);
  const [isViewPatientOpen, setIsViewPatientOpen] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  
  // Form state
  const [patientForm, setPatientForm] = useState({
    full_name: '',
    mrn_member_id: '',
    dob: '',
    gender: '',
    primary_email: '',
    primary_phone: '',
    country_code: '+91',
    address_line1: '',
    address_city: '',
    address_state: '',
    address_postal_code: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
    // Merago API fields
    merago_user_id: '',
    merago_member_id: '',
    merago_member_number: '',
  });

  const pageSize = 20;

  // Get country info by code
  const getCountryInfo = (code) => {
    return COUNTRY_CODES.find(c => c.code === code) || COUNTRY_CODES[0];
  };

  // Validate phone number based on country code
  const validatePhoneNumber = (phone, countryCode) => {
    if (!phone) return { valid: true, message: '' }; // Phone is optional
    
    const digitsOnly = phone.replace(/\D/g, '');
    const countryInfo = getCountryInfo(countryCode);
    
    if (digitsOnly.length < countryInfo.minDigits || digitsOnly.length > countryInfo.maxDigits) {
      if (countryInfo.minDigits === countryInfo.maxDigits) {
        return { 
          valid: false, 
          message: `Phone number for ${countryInfo.country} must be exactly ${countryInfo.minDigits} digits` 
        };
      } else {
        return { 
          valid: false, 
          message: `Phone number for ${countryInfo.country} must be between ${countryInfo.minDigits} and ${countryInfo.maxDigits} digits` 
        };
      }
    }
    
    return { valid: true, message: '' };
  };

  // Call Merago API to validate/create member
  const validateOrCreateMeragoMember = async (formData) => {
    const countryInfo = getCountryInfo(formData.country_code);
    const mobileNumber = formData.primary_phone.replace(/\D/g, '');
    const encodedCountryCode = encodeURIComponent(formData.country_code);
    
    // Step 1: Check if member exists
    const validateUrl = `${MERAGO_API_BASE}/validateMobileNumber?countryCode=${encodedCountryCode}&mobileNumber=${mobileNumber}&emailId=`;
    
    try {
      const response = await axios.post(validateUrl);
      const data = response.data;
      
      if (data.members && data.members.length > 0) {
        // Member exists - extract details
        const member = data.members[0];
        return {
          success: true,
          exists: true,
          userId: member.id,
          memberId: member.memberId,
          memberNumber: member.memberNumber,
        };
      } else {
        // Member doesn't exist - create new member
        return await createMeragoMember(formData, countryInfo, mobileNumber);
      }
    } catch (error) {
      console.error('Merago API error:', error);
      // Continue without Merago integration if API fails
      return { success: false, error: error.message };
    }
  };

  // Create new member in Merago
  const createMeragoMember = async (formData, countryInfo, mobileNumber) => {
    const encodedCountryCode = encodeURIComponent(formData.country_code);
    
    // Split full name into first and last name
    const nameParts = formData.full_name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Gender: Male = 0, Female = 1
    const gender = formData.gender === 'male' ? '0' : formData.gender === 'female' ? '1' : '0';
    
    // Format DOB as YYYY-MM-dd
    const dob = formData.dob || '1990-01-01';
    
    // Build create URL
    let createUrl = `${MERAGO_API_BASE}/validateMobileNumber?countryCode=${encodedCountryCode}&mobileNumber=${mobileNumber}&emailId=`;
    createUrl += `&firstName=${encodeURIComponent(firstName)}`;
    if (lastName) {
      createUrl += `&lastName=${encodeURIComponent(lastName)}`;
    }
    createUrl += `&isProvider=true`;
    createUrl += `&countryMobileCode=${countryInfo.mobileCode}`;
    createUrl += `&gender=${gender}`;
    createUrl += `&dateOfBirth=${dob}`;
    
    try {
      const response = await axios.post(createUrl);
      const data = response.data;
      
      if (data.members && data.members.length > 0) {
        const member = data.members[0];
        return {
          success: true,
          exists: false,
          created: true,
          userId: member.id,
          memberId: member.memberId,
          memberNumber: member.memberNumber,
        };
      } else {
        return { success: false, error: 'Failed to create member in Merago' };
      }
    } catch (error) {
      console.error('Merago create member error:', error);
      return { success: false, error: error.message };
    }
  };

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', pageSize);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/patients?${params.toString()}`);
      setPatients(response.data.patients || []);
      setTotalPatients(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [api, currentPage, searchQuery]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const fetchPatientActivities = async (patientId) => {
    setLoadingActivities(true);
    try {
      const response = await api.get(`/patients/${patientId}/activities`);
      setPatientActivities(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch patient activities:', error);
      // Set empty activities if endpoint doesn't exist yet
      setPatientActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPatients();
  };

  const resetForm = () => {
    setPatientForm({
      full_name: '',
      mrn_member_id: '',
      dob: '',
      gender: '',
      primary_email: '',
      primary_phone: '',
      country_code: '+91',
      address_line1: '',
      address_city: '',
      address_state: '',
      address_postal_code: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      notes: '',
      merago_user_id: '',
      merago_member_id: '',
      merago_member_number: '',
    });
  };

  const handleAddPatient = async (e) => {
    e.preventDefault();
    if (!patientForm.full_name) {
      toast.error('Patient name is required');
      return;
    }
    
    // Validate phone number if provided
    if (patientForm.primary_phone) {
      const phoneValidation = validatePhoneNumber(patientForm.primary_phone, patientForm.country_code);
      if (!phoneValidation.valid) {
        toast.error(phoneValidation.message);
        return;
      }
    }
    
    setSavingPatient(true);
    
    try {
      let meragoData = null;
      
      // If phone number is provided, call Merago API
      if (patientForm.primary_phone) {
        toast.info('Validating with Merago...');
        meragoData = await validateOrCreateMeragoMember(patientForm);
        
        if (meragoData.success) {
          if (meragoData.exists) {
            toast.success('Existing member found in Merago');
          } else if (meragoData.created) {
            toast.success('New member created in Merago');
          }
        } else {
          toast.warning('Merago validation skipped: ' + (meragoData.error || 'API unavailable'));
        }
      }
      
      // Build full phone number with country code
      const fullPhone = patientForm.primary_phone 
        ? `${patientForm.country_code}${patientForm.primary_phone.replace(/\D/g, '')}`
        : '';
      
      const payload = {
        full_name: patientForm.full_name,
        mrn_member_id: meragoData?.memberNumber || patientForm.mrn_member_id,
        dob: patientForm.dob,
        gender: patientForm.gender,
        primary_email: patientForm.primary_email,
        primary_phone: fullPhone,
        country_code: patientForm.country_code,
        address_line1: patientForm.address_line1,
        address_city: patientForm.address_city,
        address_state: patientForm.address_state,
        address_postal_code: patientForm.address_postal_code,
        emergency_contact_name: patientForm.emergency_contact_name,
        emergency_contact_phone: patientForm.emergency_contact_phone,
        notes: patientForm.notes,
        org_id: user.org_id,
        status: 'active',
        // Merago fields
        merago_user_id: meragoData?.userId || '',
        merago_member_id: meragoData?.memberId || '',
        merago_member_number: meragoData?.memberNumber || '',
      };
      
      // Remove empty fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });
      
      await api.post('/patients/', payload);
      toast.success('Patient added successfully');
      setIsAddPatientOpen(false);
      resetForm();
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add patient');
    } finally {
      setSavingPatient(false);
    }
  };

  const handleEditPatient = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return;
    
    // Validate phone number if provided
    if (patientForm.primary_phone) {
      const phoneValidation = validatePhoneNumber(patientForm.primary_phone, patientForm.country_code);
      if (!phoneValidation.valid) {
        toast.error(phoneValidation.message);
        return;
      }
    }
    
    setSavingPatient(true);
    try {
      // Build full phone number with country code
      const fullPhone = patientForm.primary_phone 
        ? `${patientForm.country_code}${patientForm.primary_phone.replace(/\D/g, '')}`
        : '';
      
      const payload = {
        full_name: patientForm.full_name,
        mrn_member_id: patientForm.mrn_member_id,
        dob: patientForm.dob,
        gender: patientForm.gender,
        primary_email: patientForm.primary_email,
        primary_phone: fullPhone,
        country_code: patientForm.country_code,
        address_line1: patientForm.address_line1,
        address_city: patientForm.address_city,
        address_state: patientForm.address_state,
        address_postal_code: patientForm.address_postal_code,
        emergency_contact_name: patientForm.emergency_contact_name,
        emergency_contact_phone: patientForm.emergency_contact_phone,
        notes: patientForm.notes,
      };
      
      // Remove empty fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });
      
      await api.patch(`/patients/${selectedPatient.patient_id}`, payload);
      toast.success('Patient updated successfully');
      setIsEditPatientOpen(false);
      setSelectedPatient(null);
      resetForm();
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update patient');
    } finally {
      setSavingPatient(false);
    }
  };

  const openViewPatient = async (patient) => {
    setSelectedPatient(patient);
    setIsViewPatientOpen(true);
    await fetchPatientActivities(patient.patient_id);
  };

  const openEditPatient = async (patient) => {
    setSelectedPatient(patient);
    setSavingPatient(true); // Use as loading indicator
    
    try {
      // Fetch full patient data from API
      const response = await api.get(`/patients/${patient.patient_id}`);
      const fullPatient = response.data;
      
      console.log('Full patient data:', fullPatient);
      
      // Extract country code from stored phone if present
      let countryCode = fullPatient.country_code || '+91';
      let phoneNumber = fullPatient.primary_phone || '';
      
      // If phone starts with a country code, extract it
      if (phoneNumber && !fullPatient.country_code) {
        for (const cc of COUNTRY_CODES) {
          if (phoneNumber.startsWith(cc.code)) {
            countryCode = cc.code;
            phoneNumber = phoneNumber.slice(cc.code.length);
            break;
          }
        }
      } else if (phoneNumber && fullPatient.country_code) {
        // If country_code is stored, phone might still have it prepended
        if (phoneNumber.startsWith(fullPatient.country_code)) {
          phoneNumber = phoneNumber.slice(fullPatient.country_code.length);
        }
      }
      
      // Remove any non-digit characters from phone
      phoneNumber = phoneNumber.replace(/\D/g, '');
      
      console.log('Extracted - countryCode:', countryCode, 'phone:', phoneNumber, 'gender:', fullPatient.gender, 'email:', fullPatient.primary_email);
      
      setPatientForm({
        full_name: fullPatient.full_name || '',
        mrn_member_id: fullPatient.mrn_member_id || '',
        dob: fullPatient.dob ? fullPatient.dob.split('T')[0] : '',
        gender: fullPatient.gender || '',
        primary_email: fullPatient.primary_email || '',
        primary_phone: phoneNumber,
        country_code: countryCode,
        address_line1: fullPatient.address_line1 || '',
        address_city: fullPatient.address_city || '',
        address_state: fullPatient.address_state || '',
        address_postal_code: fullPatient.address_postal_code || '',
        emergency_contact_name: fullPatient.emergency_contact_name || '',
        emergency_contact_phone: fullPatient.emergency_contact_phone || '',
        notes: fullPatient.notes || '',
        merago_user_id: fullPatient.merago_user_id || '',
        merago_member_id: fullPatient.merago_member_id || '',
        merago_member_number: fullPatient.merago_member_number || '',
      });
      setIsEditPatientOpen(true);
    } catch (error) {
      console.error('Failed to fetch patient details:', error);
      toast.error('Failed to load patient details');
    } finally {
      setSavingPatient(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const totalPages = Math.ceil(totalPatients / pageSize);

  if (loading && patients.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Patient Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage patients for your organization
          </p>
        </div>
        <Button onClick={() => { resetForm(); setIsAddPatientOpen(true); }} data-testid="add-patient-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Patient
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalPatients}</p>
                <p className="text-xs text-muted-foreground">Total Patients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Activity className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalPatients}</p>
                <p className="text-xs text-muted-foreground">Active Patients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">-</p>
                <p className="text-xs text-muted-foreground">Reports This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">-</p>
                <p className="text-xs text-muted-foreground">Critical Alerts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by name, MRN, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="patient-search"
              />
            </div>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Patient List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Patients</CardTitle>
          <CardDescription>
            {totalPatients} patient{totalPatients !== 1 ? 's' : ''} in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {patients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No patients found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { resetForm(); setIsAddPatientOpen(true); }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Your First Patient
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>MRN</TableHead>
                    <TableHead>DOB</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow 
                      key={patient.patient_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openViewPatient(patient)}
                      data-testid={`patient-row-${patient.patient_id}`}
                    >
                      <TableCell>
                        <div className="font-medium">{patient.full_name}</div>
                        {patient.primary_email && (
                          <div className="text-xs text-muted-foreground">{patient.primary_email}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{patient.mrn_member_id || '-'}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(patient.dob)}</TableCell>
                      <TableCell className="capitalize">{patient.gender || '-'}</TableCell>
                      <TableCell>
                        {patient.primary_phone || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={patient.status === 'active' ? 'default' : 'secondary'}>
                          {patient.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              openViewPatient(patient);
                            }}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              openEditPatient(patient);
                            }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/?patient_id=${patient.patient_id}`);
                            }}>
                              <FileText className="w-4 h-4 mr-2" />
                              View Reports
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPatients)} of {totalPatients}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Patient Dialog */}
      <Dialog open={isAddPatientOpen} onOpenChange={setIsAddPatientOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Patient</DialogTitle>
            <DialogDescription>
              Enter patient information to create a new record
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPatient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={patientForm.full_name}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mrn">MRN / Member ID</Label>
                <Input
                  id="mrn"
                  value={patientForm.mrn_member_id}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, mrn_member_id: e.target.value }))}
                  placeholder="MRN-12345"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={patientForm.dob}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, dob: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={patientForm.gender}
                  onValueChange={(value) => setPatientForm(prev => ({ ...prev, gender: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={patientForm.primary_email}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, primary_email: e.target.value }))}
                  placeholder="patient@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <div className="flex gap-2">
                  <Select
                    value={patientForm.country_code}
                    onValueChange={(value) => setPatientForm(prev => ({ ...prev, country_code: value }))}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="+91" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.code} {country.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone"
                    value={patientForm.primary_phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setPatientForm(prev => ({ ...prev, primary_phone: value }));
                    }}
                    placeholder="9880035185"
                    className="flex-1"
                  />
                </div>
                {patientForm.primary_phone && (
                  <p className="text-xs text-muted-foreground">
                    {(() => {
                      const validation = validatePhoneNumber(patientForm.primary_phone, patientForm.country_code);
                      const countryInfo = getCountryInfo(patientForm.country_code);
                      if (!validation.valid) {
                        return <span className="text-red-500">{validation.message}</span>;
                      }
                      return `${countryInfo.minDigits === countryInfo.maxDigits ? countryInfo.minDigits : `${countryInfo.minDigits}-${countryInfo.maxDigits}`} digits required for ${countryInfo.country}`;
                    })()}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={patientForm.address_line1}
                onChange={(e) => setPatientForm(prev => ({ ...prev, address_line1: e.target.value }))}
                placeholder="123 Main St"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={patientForm.address_city}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, address_city: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={patientForm.address_state}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, address_state: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal">Postal Code</Label>
                <Input
                  id="postal"
                  value={patientForm.address_postal_code}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, address_postal_code: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergency_name">Emergency Contact Name</Label>
                <Input
                  id="emergency_name"
                  value={patientForm.emergency_contact_name}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_phone">Emergency Contact Phone</Label>
                <Input
                  id="emergency_phone"
                  value={patientForm.emergency_contact_phone}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={patientForm.notes}
                onChange={(e) => setPatientForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes about the patient..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddPatientOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingPatient}>
                {savingPatient && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Patient
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Patient Dialog */}
      <Dialog open={isEditPatientOpen} onOpenChange={setIsEditPatientOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Patient</DialogTitle>
            <DialogDescription>
              Update patient information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditPatient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_full_name">Full Name *</Label>
                <Input
                  id="edit_full_name"
                  value={patientForm.full_name}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_mrn">MRN / Member ID</Label>
                <Input
                  id="edit_mrn"
                  value={patientForm.mrn_member_id}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, mrn_member_id: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_dob">Date of Birth</Label>
                <Input
                  id="edit_dob"
                  type="date"
                  value={patientForm.dob}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, dob: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={patientForm.gender}
                  onValueChange={(value) => setPatientForm(prev => ({ ...prev, gender: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={patientForm.primary_email}
                  onChange={(e) => setPatientForm(prev => ({ ...prev, primary_email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Phone</Label>
                <div className="flex gap-2">
                  <Select
                    value={patientForm.country_code}
                    onValueChange={(value) => setPatientForm(prev => ({ ...prev, country_code: value }))}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="+91" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.code} {country.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="edit_phone"
                    value={patientForm.primary_phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setPatientForm(prev => ({ ...prev, primary_phone: value }));
                    }}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditPatientOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingPatient}>
                {savingPatient && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Patient Detail Dialog */}
      <Dialog open={isViewPatientOpen} onOpenChange={setIsViewPatientOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-bold text-primary">
                  {selectedPatient?.full_name?.charAt(0) || 'P'}
                </span>
              </div>
              <div>
                <span>{selectedPatient?.full_name}</span>
                <p className="text-sm font-normal text-muted-foreground">
                  MRN: {selectedPatient?.mrn_member_id || 'Not assigned'}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="details" className="mt-4">
            <TabsList>
              <TabsTrigger value="details">Patient Details</TabsTrigger>
              <TabsTrigger value="activities">Activities</TabsTrigger>
              <TabsTrigger value="reports">Reports</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase">Personal Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        <strong>DOB:</strong> {formatDate(selectedPatient?.dob)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm capitalize">
                        <strong>Gender:</strong> {selectedPatient?.gender || '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase">Contact Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedPatient?.primary_email || '-'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedPatient?.primary_phone || '-'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        {[selectedPatient?.address_line1, selectedPatient?.address_city, selectedPatient?.address_state].filter(Boolean).join(', ') || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-medium text-sm text-muted-foreground uppercase mb-3">Emergency Contact</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-sm">
                    <strong>Name:</strong> {selectedPatient?.emergency_contact_name || '-'}
                  </div>
                  <div className="text-sm">
                    <strong>Phone:</strong> {selectedPatient?.emergency_contact_phone || '-'}
                  </div>
                </div>
              </div>

              {selectedPatient?.notes && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase mb-2">Notes</h4>
                  <p className="text-sm">{selectedPatient.notes}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="activities" className="mt-4">
              {loadingActivities ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : patientActivities.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No recent activities</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {patientActivities.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                      <div className={`p-1.5 rounded-full ${
                        activity.type === 'report' ? 'bg-blue-100' :
                        activity.type === 'alert' ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        {activity.type === 'report' ? (
                          <FileText className="w-4 h-4 text-blue-600" />
                        ) : activity.type === 'alert' ? (
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{activity.title}</p>
                        <p className="text-xs text-muted-foreground">{activity.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="reports" className="mt-4">
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">View all reports for this patient</p>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setIsViewPatientOpen(false);
                    navigate(`/?patient_id=${selectedPatient?.patient_id}`);
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Go to Reports
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsViewPatientOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setIsViewPatientOpen(false);
              openEditPatient(selectedPatient);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Patient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientManagementPage;
